import React from "react";

import { useTranslation } from "react-i18next";
import Select from "react-select";

import SwitchOnOff from "../../common/tools/SwitchOnOff";

import type { JSX } from "react";
import type { SingleValue } from "react-select";

import type {
  DisplayOrder,
  DisplayOrderValue,
} from "../../../api/ProjectAPI.types";

const DEFAULT_ORDER_LIST: DisplayOrderValue[] = [
  "random_load",
  "random_display",
  "alphabetical",
  "updated_date",
  "reverse_ranking",
];

interface DisplayOrderOption {
  value: DisplayOrderValue;
  label: string;
}

interface ComponentDisplayOrderProps {
  data?: DisplayOrder;
  onChange: (displayOrder: DisplayOrder) => void;
  disabled?: boolean;
  hasPublicationDateOrder?: boolean;
}

export default function ComponentDisplayOrder({
  data,
  onChange,
  disabled,
  hasPublicationDateOrder,
}: ComponentDisplayOrderProps): JSX.Element {
  const { t } = useTranslation("editor-platform");

  const orderList: DisplayOrderValue[] = hasPublicationDateOrder
    ? [...DEFAULT_ORDER_LIST, "publication_date"]
    : DEFAULT_ORDER_LIST;
  const orderOptionList: DisplayOrderOption[] = orderList.map((order) => ({
    value: order,
    label: t(`display_order_${order}`),
  }));

  const enabled = Boolean(data?.enabled);
  const currentOrder = data?.order ?? "random_load";
  const selectedOption =
    orderOptionList.find((option) => option.value === currentOrder) ?? null;

  const handleSwitch = (checked: boolean): void => {
    onChange({ enabled: checked, order: currentOrder });
  };

  const handleSelect = (option: SingleValue<DisplayOrderOption>): void => {
    if (!option) return;
    onChange({ enabled, order: option.value });
  };

  return (
    <React.Fragment>
      <div className="editor-form-row editor-form-row-inline">
        <div className="editor-form-input">
          <SwitchOnOff
            checked={enabled}
            disabled={disabled}
            name="display_order_switch"
            onChange={handleSwitch}
            size="sm"
          />
        </div>
        <div className="editor-form-label">{t("displayOrder")}</div>
      </div>
      <div className="display-order-select">
        <Select
          closeMenuOnSelect
          className="editor-select"
          classNamePrefix="slct"
          isDisabled={!enabled}
          name="display_order"
          onChange={handleSelect}
          options={orderOptionList}
          placeholder={t("select")}
          value={selectedOption}
        />
      </div>
    </React.Fragment>
  );
}
